import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Responsive behaviour of the desktop sidebar (NavBarDesktopV3).
 *
 * Below 1024px the sidebar is not rendered at all (NavBar switches to NavBarMobile), so this hook
 * only describes what happens from 1024px up:
 *
 *   | Viewport      | Layout | Toggle does                                                      |
 *   |---------------|--------|------------------------------------------------------------------|
 *   | 1024–1279     | drawer | opens the labelled menu FLOATING over the content (no reflow)     |
 *   | >= 1280       | push   | expands/collapses the in-flow sidebar (merchant preference)       |
 *
 * The compact band used to force the icon rail AND hide the toggle, which left the merchant with a
 * column of unlabelled icons and no way out (hover tooltips do nothing on a touch tablet). The rail
 * stays — it is the right resting state at that width — but the toggle now opens a temporary
 * overlay drawer, so the content area never loses a pixel and the labels are always one tap away.
 */
export const SIDEBAR_PUSH_MIN_WIDTH = 1280;

/** Id of the sidebar panel, referenced by the toggle through `aria-controls`. */
export const SIDEBAR_PANEL_ID = 'sidebar-nav-panel';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// SSR/jsdom guard, consistent with BusinessClosedBanner: `window` may be missing and `innerWidth`
// may be 0, so both initial values fall back to the collapsed (safest) reading.
const readViewportWidth = () => (typeof window !== 'undefined' ? window.innerWidth : SIDEBAR_PUSH_MIN_WIDTH);

export const useSidebarMode = () => {
  const [viewportWidth, setViewportWidth] = useState(readViewportWidth);
  // Merchant's manual collapse preference — only meaningful at >= 1280.
  const [userExpanded, setUserExpanded] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= SIDEBAR_PUSH_MIN_WIDTH : false,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isCompactDesktop = viewportWidth < SIDEBAR_PUSH_MIN_WIDTH;
  // In the compact band the expanded sidebar is a temporary drawer; above it, the in-flow sidebar.
  const isExpanded = isCompactDesktop ? drawerOpen : userExpanded;
  const isDrawerOpen = isCompactDesktop && drawerOpen;

  const toggleSidebar = useCallback(() => {
    if (isCompactDesktop) setDrawerOpen((open) => !open);
    else setUserExpanded((expanded) => !expanded);
  }, [isCompactDesktop]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Leaving the compact band hands control back to the merchant preference: a drawer left open
  // would otherwise float over a sidebar that is already expanded in flow.
  useEffect(() => {
    if (!isCompactDesktop) setDrawerOpen(false);
  }, [isCompactDesktop]);

  // Esc closes the drawer and returns focus to the toggle that opened it.
  useEffect(() => {
    if (!isDrawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setDrawerOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDrawerOpen]);

  // Focus trap: the drawer is modal (aria-modal), so Tab must cycle inside it.
  useEffect(() => {
    if (!isDrawerOpen) return;
    const panel = panelRef.current;
    if (!panel) return;
    panel.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    panel.addEventListener('keydown', onKeyDown);
    return () => panel.removeEventListener('keydown', onKeyDown);
  }, [isDrawerOpen]);

  return {
    /** 1024–1279: the sidebar rests as an icon rail and expands as a temporary drawer. */
    isCompactDesktop,
    /** Labels are visible (drawer open in the compact band, or expanded sidebar at >= 1280). */
    isExpanded,
    /** The expanded sidebar is floating over the content instead of pushing it. */
    isDrawerOpen,
    toggleSidebar,
    closeDrawer,
    toggleRef,
    panelRef,
    panelId: SIDEBAR_PANEL_ID,
  };
};
